import React, {
  forwardRef,
  RefObject,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import { Button, Modal } from 'react-bootstrap'
import { convertToDateTime, maxEndDate } from '../service/helpers/DateUtilities'
import DatePicker from './DatePicker'
import { IModalReference } from 'shared/models/References'

interface IProps {
  onPeriodSelected: (startDate: Date, endDate: Date) => void
  onHide?: () => void
  onShow?: () => void
  initialStartDate?: Date
  initialEndDate?: Date
  maxEndDate?: Date
  disabledStartDate?: boolean
  modalStack?: boolean
  canClose: boolean
  errorMessage?: string
}

function PeriodSelect(props: IProps, ref: RefObject<IModalReference>) {
  const [showModal, setShowModal] = useState<boolean>(false)
  const [startDate, setStartDate] = useState(new Date())
  const [endDate, setEndDate] = useState(new Date())
  const effectiveMaxEndDate = props.maxEndDate ?? maxEndDate

  const [errorMessage, setErrorMessage] = useState(props.errorMessage)
  const [awaitClose, setAwaitClose] = useState(false)

  useImperativeHandle(ref, () => ({
    show: () => {
      show()
    },
    hide: () => {
      hide()
    },
  }))

  useEffect(() => {
    if (props.canClose && awaitClose) {
      hide()
      setAwaitClose(false)
    }
  }, [props.canClose, awaitClose])

  useEffect(() => {
    setStartDate(
      !!props.initialStartDate
        ? convertToDateTime(props.initialStartDate)
        : new Date()
    )
    setEndDate(
      !!props.initialEndDate
        ? convertToDateTime(props.initialEndDate)
        : new Date()
    )
  }, [props.initialEndDate, props.initialStartDate])

  useEffect(() => {
    setErrorMessage(props.errorMessage)
  }, [props.errorMessage, errorMessage])

  const clearState = () => {
    setAwaitClose(false)
    setStartDate(
      !!props.initialStartDate
        ? convertToDateTime(props.initialStartDate)
        : new Date()
    )
    setEndDate(
      !!props.initialEndDate
        ? convertToDateTime(props.initialEndDate)
        : new Date()
    )
    setErrorMessage(null)
  }

  const show = () => {
    if (!showModal) {
      clearState()
      setShowModal(true)
      if (!!props.onShow) {
        props.onShow()
      }
    }
  }

  const selectPeriod = () => {
    if (startDate > endDate) {
      setErrorMessage('Startdatum mag niet na de einddatum liggen.')
    } else {
      setErrorMessage(null)
      setAwaitClose(true)
      props.onPeriodSelected(startDate, endDate)
    }
  }

  const hide = () => {
    setShowModal(false)
    if (!!props.onHide) {
      props.onHide()
    }
  }

  return (
    <React.Fragment>
      {showModal && (
        <Modal
          show={showModal}
          onHide={hide}
          animation={false}
          className={props.modalStack ? 'modal-stack2' : ''}
        >
          <Modal.Header closeButton>
            <Modal.Title>Bevestig ingangsdatum en einddatum</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {errorMessage != null && (
              <div className='alert alert-danger'>{errorMessage}</div>
            )}
            <table>
              <thead>
                <tr>
                  <th>Ingangsdatum</th>
                  <th>Einddatum</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <DatePicker
                      date={startDate}
                      onDateChanged={setStartDate}
                      maxEndDate={effectiveMaxEndDate}
                      disabled={props.disabledStartDate}
                      name='ingangsdatum'
                    />
                  </td>
                  <td>
                    <DatePicker
                      date={endDate}
                      onDateChanged={setEndDate}
                      maxEndDate={effectiveMaxEndDate}
                      name='einddatum'
                      showMaxDate={true}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </Modal.Body>
          <Modal.Footer>
            <Button variant='primary' onClick={selectPeriod}>
              Bevestig
            </Button>
            <Button variant='secondary' onClick={hide}>
              Sluiten
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </React.Fragment>
  )
}
export default forwardRef(PeriodSelect)
